import { useState } from "react";
import Shell from "@/layout/Shell";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn, fmtDate } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Badge from "@/components/Badge";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const HALL_COLORS = { BSN:"#0038C8", APJ:"#10B981", A226:"#8B5CF6", A116:"#F59E0B", AMPH:"#EC4899", EST:"#06B6D4", LBA:"#F43F5E" };

function getHallColor(name) {
  const key = Object.keys(HALL_COLORS).find(k => (name || "").toUpperCase().includes(k));
  return key ? HALL_COLORS[key] : "#64748b";
}

export default function CalendarView() {
  const today = new Date();
  const [cur, setCur] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [dayModal, setDayModal] = useState(null); // { date, bookings }

  const startDate = `${cur.year}-${String(cur.month + 1).padStart(2, "0")}-01`;
  const endDate   = `${cur.year}-${String(cur.month + 1).padStart(2, "0")}-${new Date(cur.year, cur.month + 1, 0).getDate()}`;

  const { data, loading } = useFetch(() => api.calendarData(startDate, endDate), [startDate]);
  const bookings = data?.events || data?.bookings || [];
  const byDate   = bookings.reduce((acc, b) => { (acc[b.date] = acc[b.date] || []).push(b); return acc; }, {});

  const daysInMonth = new Date(cur.year, cur.month + 1, 0).getDate();
  const firstDay    = new Date(cur.year, cur.month, 1).getDay();
  const cells = [...Array(firstDay).fill(null), ...Array(daysInMonth).fill(0).map((_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);

  const prev = () => setCur(c => c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 });
  const next = () => setCur(c => c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 });

  const openDay = async (day) => {
    if (!day) return;
    const dateStr = `${cur.year}-${String(cur.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dayBookings = byDate[dateStr] || [];
    // Also try to get events for that day
    setDayModal({ date: dateStr, bookings: dayBookings, loading: true });
    try {
      const evs = await api.eventsByDate(dateStr);
      setDayModal(d => d && d.date === dateStr ? { ...d, events: evs?.events || [], loading: false } : d);
    } catch { setDayModal(d => d && d.date === dateStr ? { ...d, loading: false } : d); }
  };

  return (
    <Shell role="portal" title="Booking calendar" subtitle="All confirmed bookings across halls">
      <div className="surface rounded-2xl p-4 lg:p-5">
        {/* Navigation */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={prev}><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="font-bold text-base sm:text-lg flex-1 text-center tracking-tight">{MONTHS[cur.month]} {cur.year}</h2>
          <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={next}><ChevronRight className="h-4 w-4" /></Button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(HALL_COLORS).map(([k, c]) => (
            <span key={k} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c }} />{k}
            </span>
          ))}
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map(d => <div key={d} className="text-[10px] font-semibold text-center text-muted-foreground py-1">{d}</div>)}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-7 gap-1">{[...Array(35)].map((_, i) => <div key={i} className="h-12 rounded-lg shimmer" />)}</div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} />;
              const ds = `${cur.year}-${String(cur.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const dayBs = byDate[ds] || [];
              const isToday = today.getFullYear() === cur.year && today.getMonth() === cur.month && today.getDate() === day;
              return (
                <button key={day} onClick={() => openDay(day)}
                  className={cn("min-h-12 sm:min-h-16 rounded-xl p-0.5 sm:p-1 text-left transition-all duration-150 border",
                    isToday
                      ? "bg-blue-50 dark:bg-blue-950/30 border-blue-500/40 ring-1 ring-blue-500/20"
                      : "bg-transparent border-transparent hover:bg-muted hover:border-border"
                  )}
                >
                  <span className={cn("block text-right text-[10px] font-medium mb-0.5 pr-0.5", isToday ? "text-blue-600 dark:text-blue-400 font-bold" : "text-muted-foreground")}>{day}</span>
                  {dayBs.slice(0, 3).map((b, j) => (
                    <div key={j} className="truncate text-[8px] sm:text-[9px] font-medium px-1 py-0.5 rounded mb-0.5 text-white leading-tight"
                      style={{ background: getHallColor(b.hall_name || b.auditorium) }}>
                      {b.eventName || b.bookingID}
                    </div>
                  ))}
                  {dayBs.length > 3 && <div className="text-[9px] text-muted-foreground px-1">+{dayBs.length - 3}</div>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Day detail modal */}
      <Dialog open={!!dayModal} onOpenChange={v => !v && setDayModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold">{dayModal && fmtDate(dayModal.date)}</DialogTitle>
          </DialogHeader>
          {dayModal && (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {dayModal.loading && <div className="h-12 rounded-xl shimmer" />}
              {(dayModal.bookings || []).length === 0 && !dayModal.loading && (
                <p className="text-sm text-muted-foreground text-center py-4">No bookings on this day.</p>
              )}
              {(dayModal.bookings || []).map(b => (
                <div key={b.bookingID} className="surface rounded-xl p-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate">{b.eventName || b.bookingID}</div>
                    <Badge status={b.status} />
                  </div>
                  <div className="text-xs text-muted-foreground">{b.auditorium} · {b.timeSlot}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{b.bookingID}</div>
                </div>
              ))}
              {(dayModal.events || []).length > 0 && (
                <div className="border-t border-border/40 pt-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Public events</p>
                  {(dayModal.events).map(ev => (
                    <div key={ev.id} className="surface rounded-xl p-3 mb-2">
                      <div className="font-medium text-sm">{ev.title}</div>
                      <div className="text-xs text-muted-foreground">{ev.venue} · {ev.timeSlot}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
